/**
 * useVersaoPortal — qual versão do portal esta aba carregou e qual está no ar.
 *
 * Fonte: /versao.json, gravado pelo scripts/deploy-frontend.sh ao lado do
 * index.html ({bundle, data, notas{pt,en}}). Um poller só para a aba inteira
 * (o cartão "Portal atualizado" e a linha "v. dd/mm" do menu leem daqui):
 * lê ao abrir (é a versão DESTA aba), ao voltar pro foco e a cada 5 min,
 * sempre com cache 'no-store'.
 *
 * Cuidados que vieram do essentia (16/set):
 *  - Rotas lazy: uma tela pode mudar sem o main.<hash>.js mudar — por isso a
 *    identidade da versão é o carimbo `data`, e o bundle é só o 2º sinal.
 *  - Deploy antigo sem versao.json: o nginx faz try_files e devolve o index.html
 *    com 200 — só aceita se vier como JSON de verdade; senão cai no bundle.
 *  - No `npm start` não há bundle com hash: fica desligado (aba === null).
 */
import { useEffect, useState } from 'react';

export interface VersaoPortal {
  bundle: string | null;
  data: string | null;
  notas: { pt: string[]; en: string[] };
  /** versão visível — "1.0.4" (último número sobe a cada deploy) */
  versao: string | null;
  /** número de build (git rev-list --count no deploy) — só no detalhe técnico */
  numero: number | null;
  /** novo | melhoria | correcao | seguranca | interno */
  tipo: string | null;
  /** caminho no portal que abre a mudança (só quando é tela) */
  url: string | null;
  /** o deploy pediu que a aba velha recarregue (linha "recarregar: sim" na nota) */
  recarregar: boolean;
  /** por que vale recarregar agora — uma linha, aparece ao lado do botão */
  motivoRecarregar: string | null;
  /**
   * Carimbo da ÚLTIMA versão que pediu recarga, que sobrevive aos deploys
   * seguintes. Toda aba carregada ANTES desse instante ainda precisa recarregar,
   * mesmo que o deploy de agora não tenha pedido nada.
   *
   * Por que existe (achado do terminal Dra, 28/09): o pedido morava só no
   * `recarregar` do deploy da vez, então o deploy seguinte, sem a linha na nota,
   * apagava o pedido — e a aba velha, que pode ficar viva por dias, nunca via o
   * botão. Uma aba da clínica carregada antes de 23/09 seguiu mostrando
   * "Transcrição falhou" falso até 28/09.
   */
  recarregarSeAnteriorA: string | null;
  /**
   * O motivo que a versão do marco escreveu ("recarregar: sim — <motivo>"),
   * herdado junto com ele. Sem isto, a aba velha veria o motivo de uma versão
   * sem relação com o pedido — para quem lê, parece defeito (Gerente, 28/09).
   */
  recarregarMotivo: string | null;
}

/**
 * O texto que acompanha o botão Recarregar: o motivo DAQUELE pedido — o da nota
 * de hoje, quando foi hoje que pediram, ou o motivo herdado junto com o marco.
 * Nunca a nota do dia: para uma aba velha, ler a novidade de uma versão sem
 * relação com o pedido parece defeito (Gerente, 28/09). Sem motivo guardado,
 * quem chama passa a frase genérica do idioma.
 */
export function motivoDaRecarga(
  publicada: Pick<VersaoPortal, 'motivoRecarregar' | 'recarregarMotivo'> | null,
  generica: string,
): string {
  return publicada?.motivoRecarregar || publicada?.recarregarMotivo || generica;
}

/**
 * Esta aba precisa recarregar? Duas portas, e basta uma:
 *   1. o deploy da vez pediu (`recarregar: sim` na nota);
 *   2. esta aba é anterior à última versão que pediu — o pedido não expira
 *      só porque vieram outros deploys depois dele.
 *
 * Sem o carimbo da aba (dev, ou primeira leitura ainda não feita) a resposta é
 * não: o aviso NASCE DESLIGADO, e isto aqui só impede que ele escape.
 */
export function precisaRecarregar(
  aba: Pick<VersaoPortal, 'data'> | null,
  publicada: Pick<VersaoPortal, 'recarregar' | 'recarregarSeAnteriorA'> | null,
): boolean {
  if (!publicada) return false;
  const limite = publicada.recarregarSeAnteriorA;
  // HAVENDO MARCO, ele manda sozinho, e o booleano é ignorado de propósito: o
  // deploy o deixa ligado enquanto o marco existir, só para alcançar a aba que
  // ainda roda o código antigo e não conhece o marco. Se este código também
  // olhasse o booleano, toda aba — inclusive as já atualizadas — veria o botão.
  if (limite) {
    if (!aba?.data) return false;
    const daAba = Date.parse(aba.data), oLimite = Date.parse(limite);
    if (Number.isNaN(daAba) || Number.isNaN(oLimite)) return false;
    return daAba < oLimite;
  }
  return publicada.recarregar;
}

const VAZIO = { pt: [] as string[], en: [] as string[] };
const semCarimbo = (bundle: string | null): VersaoPortal => ({ bundle, data: null, notas: VAZIO, versao: null, numero: null, tipo: null, url: null, recarregar: false, motivoRecarregar: null, recarregarSeAnteriorA: null, recarregarMotivo: null });

export interface EstadoVersao {
  /** versão que esta aba carregou (null = dev ou ainda não lida) */
  aba: VersaoPortal | null;
  /** versão servida agora pelo nginx */
  publicada: VersaoPortal | null;
  /** publicada é mais nova que a aba */
  novaVersao: boolean;
  /** identidade da versão publicada (data ou bundle) — pra "dispensar esta" */
  chave: string;
  /** bundle desta aba (main.<hash>.js) */
  bundleAba: string | null;
  /** esta aba precisa recarregar — pelo pedido da vez OU por ser anterior ao último pedido */
  exigeRecarga: boolean;
}

const BUNDLE = /\/static\/js\/main\.[a-f0-9]+\.js/;
const INTERVALO_MS = 5 * 60 * 1000;

// Simulação, SÓ em desenvolvimento: no `npm start` não existe versao.json nem
// bundle com hash, então a pílula nunca aparece no local. Abrir qualquer página
// com ?simular-versao=1 mostra a pílula (e a linha do menu) como se tivesse
// acabado de sair um deploy. Código morto no build de produção.
const SIMULAR = process.env.NODE_ENV === 'development'
  && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('simular-versao');
// ?simular-versao=recarregar testa o modo exceção (com botão Recarregar)
const SIMULAR_RECARGA = SIMULAR
  && new URLSearchParams(window.location.search).get('simular-versao') === 'recarregar';
const SIMULACAO: EstadoVersao = {
  aba: { bundle: null, data: new Date(Date.now() - 5 * 86_400_000).toISOString(), notas: VAZIO, versao: '1.0.1', numero: 1622, tipo: null, url: null, recarregar: false, motivoRecarregar: null, recarregarSeAnteriorA: null, recarregarMotivo: null },
  publicada: {
    bundle: null,
    data: new Date().toISOString(),
    notas: {
      pt: ['Cada atualização agora tem número, tipo e um botão "Abrir" que leva à tela que mudou', 'No histórico, "ver mais" separa a história pra equipe do detalhe técnico'],
      en: ['Every update now has a number, a type and an "Open" button to the screen that changed', 'In the history, "read more" separates the team story from the technical detail'],
    },
    versao: '1.0.4',
    numero: 1637,
    tipo: 'melhoria',
    url: '/admin/atualizacoes',
    recarregar: SIMULAR_RECARGA,
    motivoRecarregar: SIMULAR_RECARGA ? 'a ficha do paciente mudou de verdade' : null,
    recarregarSeAnteriorA: null,
    recarregarMotivo: SIMULAR_RECARGA ? 'a ficha do paciente mudou de verdade' : null,
  },
  novaVersao: true,
  chave: 'simulacao',
  bundleAba: null,
  exigeRecarga: SIMULAR_RECARGA,
};

function bundleDestaAba(): string | null {
  for (const s of Array.from(document.scripts)) {
    const m = (s.getAttribute('src') || '').match(BUNDLE);
    if (m) return m[0];
  }
  return null;
}

export async function lerVersaoPublicada(): Promise<VersaoPortal | null> {
  try {
    const r = await fetch(`/versao.json?v=${Date.now()}`, { cache: 'no-store' });
    const ehJson = (r.headers.get('content-type') || '').includes('application/json');
    if (r.ok && ehJson) {
      const j = JSON.parse(await r.text());
      if (j && typeof j === 'object' && !Array.isArray(j)) {
        return {
          bundle: typeof j.bundle === 'string' ? j.bundle : null,
          data: typeof j.data === 'string' ? j.data : null,
          notas: { pt: Array.isArray(j.notas?.pt) ? j.notas.pt : [], en: Array.isArray(j.notas?.en) ? j.notas.en : [] },
          versao: typeof j.versao === 'string' && /^\d+\.\d+\.\d+$/.test(j.versao) ? j.versao : null,
          numero: typeof j.numero === 'number' && j.numero > 0 ? j.numero : null,
          tipo: typeof j.tipo === 'string' && j.tipo ? j.tipo : null,
          url: typeof j.url === 'string' && j.url.startsWith('/') ? j.url : null,
          recarregar: j.recarregar === true,
          motivoRecarregar: typeof j.motivo_recarregar === 'string' && j.motivo_recarregar ? j.motivo_recarregar : null,
          recarregarSeAnteriorA: typeof j.recarregar_se_anterior_a === 'string' && j.recarregar_se_anterior_a ? j.recarregar_se_anterior_a : null,
          recarregarMotivo: typeof j.recarregar_motivo === 'string' && j.recarregar_motivo ? j.recarregar_motivo : null,
        };
      }
    }
  } catch { /* segue pro index.html */ }
  try {
    const r = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return null;
    const m = (await r.text()).match(BUNDLE);
    return m ? semCarimbo(m[0]) : null;
  } catch { return null; }
}

// ---- um poller por aba, N assinantes ----
const bundleAba = typeof document !== 'undefined' ? bundleDestaAba() : null;
let aba: VersaoPortal | null = null;
let publicada: VersaoPortal | null = null;
const ouvintes = new Set<() => void>();
let ligado = false;

function estado(): EstadoVersao {
  if (SIMULAR) return SIMULACAO;
  const novaVersao = !!publicada && (
    publicada.data && aba?.data ? publicada.data !== aba.data : !!publicada.bundle && !!bundleAba && publicada.bundle !== bundleAba
  );
  return { aba, publicada, novaVersao, chave: publicada?.data || publicada?.bundle || '', bundleAba,
           exigeRecarga: precisaRecarregar(aba, publicada) };
}

async function checar() {
  if (document.visibilityState !== 'visible') return;
  const v = await lerVersaoPublicada();
  if (!v) return;
  if (!aba) {
    // 1ª leitura = versão desta aba, salvo se o bundle já não bate (deploy entre o load e agora)
    aba = v.bundle && bundleAba && v.bundle !== bundleAba ? semCarimbo(bundleAba) : v;
  }
  publicada = v;
  ouvintes.forEach(f => f());
}

function ligar() {
  if (ligado) return; // sem bundle com hash (npm start) a leitura só não acha versao.json — inofensivo
  ligado = true;
  const aoVoltar = () => { if (document.visibilityState === 'visible') checar(); };
  window.setInterval(checar, INTERVALO_MS);
  document.addEventListener('visibilitychange', aoVoltar);
  window.addEventListener('focus', checar);
  checar();
}

export function useVersaoPortal(): EstadoVersao {
  const [e, setE] = useState<EstadoVersao>(estado);
  useEffect(() => {
    const f = () => setE(estado());
    ouvintes.add(f);
    ligar();
    f();
    return () => { ouvintes.delete(f); };
  }, []);
  return e;
}

/** "há 5 dias" / "5 days" — há quanto tempo esta aba está aberta */
export function idadeDaAba(lang: string): string {
  const ms = Date.now() - performance.timeOrigin;
  const h = Math.floor(ms / 3_600_000), d = Math.floor(h / 24);
  const plural = (n: number, um: string, mais: string) => `${n} ${n > 1 ? mais : um}`;
  if (d >= 1) return lang === 'en' ? plural(d, 'day', 'days') : lang === 'es' ? plural(d, 'día', 'días') : plural(d, 'dia', 'dias');
  if (h >= 1) return `${h} h`;
  return `${Math.max(1, Math.floor(ms / 60_000))} min`;
}
